// Quick Start Fields - Single Source of Truth
// 10-field definition per Agent System Instructions

export type QuickStartFieldType = "text" | "textarea" | "select"

export interface QuickStartFieldOption {
  value: string
  label: string
}

export interface QuickStartField {
  key: string // field slug, e.g. "business_name"
  label: string // display label
  type: QuickStartFieldType
  required: boolean
  isCore: boolean // whether it counts toward 5-of-5 brain progress
  order: number // 1..10
  helperText?: string // guidance text
  placeholder?: string // input placeholder
  options?: QuickStartFieldOption[] // for select fields
  conversationalPrompt: string // Question asked in chat flow
  tipText?: string // Additional tip for the chat flow
}

export const QUICK_START_FIELDS: QuickStartField[] = [
  {
    key: "business_name",
    label: "Business Name",
    type: "text",
    required: true,
    isCore: true,
    order: 1,
    helperText: "This is how I'll refer to your business in our conversations.",
    placeholder: "Enter your business name...",
    conversationalPrompt: "First, what's your business called?",
    tipText: "Use your official business name or brand name.",
  },
  {
    key: "what_you_do",
    label: "What You Do",
    type: "textarea",
    required: true,
    isCore: true,
    order: 2,
    helperText: "Focus on your core offerings—what you're known for.",
    placeholder: "Describe what your business does...",
    conversationalPrompt: "In a sentence or two, what does {business_name} do?",
    tipText: "Be specific about what makes your offering unique.",
  },
  {
    key: "who_you_serve",
    label: "Who You Serve",
    type: "textarea",
    required: true,
    isCore: true,
    order: 3,
    helperText: "Describe your ideal customer or client profile.",
    placeholder: "Describe your typical customers...",
    conversationalPrompt: "Who are your typical customers? What kind of people or companies do you serve?",
    tipText: "Include demographics, industry, or specific pain points they have.",
  },
  {
    key: "biggest_challenge",
    label: "Biggest Challenge",
    type: "textarea",
    required: true,
    isCore: true,
    order: 4,
    helperText: "What keeps you up at night?",
    placeholder: "Describe your biggest business challenge...",
    conversationalPrompt: "What's the single biggest challenge your business is facing right now?",
    tipText: "Be honest—this helps me give you relevant advice.",
  },
  {
    key: "primary_goal",
    label: "Primary Goal (next 12 months)",
    type: "textarea",
    required: true,
    isCore: true,
    order: 5,
    helperText: "This helps me tailor strategic advice to what matters most.",
    placeholder: "What do you want to achieve?",
    conversationalPrompt: "What is your primary business goal for the next 12 months?",
    tipText: "Think about what success looks like in the next year.",
  },
  {
    key: "years_in_business",
    label: "Years in Business",
    type: "select",
    required: true,
    isCore: false,
    order: 6,
    helperText: "How long have you been operating?",
    conversationalPrompt: "How long has {business_name} been in business?",
    options: [
      { value: "pre-launch", label: "Pre-launch / Idea stage" },
      { value: "less-than-1", label: "Less than 1 year" },
      { value: "1-3", label: "1-3 years" },
      { value: "3-5", label: "3-5 years" },
      { value: "5-10", label: "5-10 years" },
      { value: "10-plus", label: "10+ years" },
    ],
  },
  {
    key: "team_size",
    label: "Team Size",
    type: "select",
    required: true,
    isCore: false,
    order: 7,
    helperText: "Include yourself and any employees or contractors.",
    conversationalPrompt: "How many people work at {business_name}, including yourself?",
    options: [
      { value: "solo", label: "Just me (Solo)" },
      { value: "2-5", label: "2-5 people" },
      { value: "6-10", label: "6-10 people" },
      { value: "11-25", label: "11-25 people" },
      { value: "26-50", label: "26-50 people" },
      { value: "50-plus", label: "50+ people" },
    ],
  },
  {
    key: "revenue_range",
    label: "Revenue Range",
    type: "select",
    required: true,
    isCore: false,
    order: 8,
    helperText: "Approximate annual revenue helps me tailor advice.",
    conversationalPrompt: "What's your approximate annual revenue range?",
    options: [
      { value: "pre-revenue", label: "Pre-revenue" },
      { value: "under-100k", label: "Under $100K" },
      { value: "100k-500k", label: "$100K - $500K" },
      { value: "500k-1m", label: "$500K - $1M" },
      { value: "1m-5m", label: "$1M - $5M" },
      { value: "5m-plus", label: "$5M+" },
    ],
  },
  {
    key: "one_question",
    label: "One Question",
    type: "textarea",
    required: true,
    isCore: false,
    order: 9,
    helperText: "What's the one thing you'd love clarity on?",
    placeholder: "Ask me anything about your business...",
    conversationalPrompt: "If you could get expert advice on one question about your business, what would it be?",
    tipText: "This gives me a starting point for how I can help you most.",
  },
  {
    key: "referral_source",
    label: "How Did You Hear About BizzIQ?",
    type: "select",
    required: false,
    isCore: false,
    order: 10,
    helperText: "Optional - helps us improve.",
    conversationalPrompt: "Quick side question — how did you hear about BizzIQ?",
    options: [
      { value: "search", label: "Search engine (Google, Bing, etc.)" },
      { value: "social", label: "Social media" },
      { value: "friend", label: "Friend or colleague" },
      { value: "podcast", label: "Podcast" },
      { value: "article", label: "Blog or article" },
      { value: "other", label: "Other" },
    ],
  },
]

// Core fields only (for Brain 0/5 progress)
export const CORE_QUICK_START_FIELDS = QUICK_START_FIELDS.filter((f) => f.isCore)

// Extended fields (non-core)
export const EXTENDED_QUICK_START_FIELDS = QUICK_START_FIELDS.filter((f) => !f.isCore)

// Total counts
export const TOTAL_QUICK_START_FIELDS = QUICK_START_FIELDS.length
export const TOTAL_CORE_FIELDS = CORE_QUICK_START_FIELDS.length

// Helper functions
export function getQuickStartFieldByKey(key: string): QuickStartField | undefined {
  return QUICK_START_FIELDS.find((f) => f.key === key)
}

export function getQuickStartFieldByOrder(order: number): QuickStartField | undefined {
  return QUICK_START_FIELDS.find((f) => f.order === order)
}

export function isFieldCore(key: string): boolean {
  const field = getQuickStartFieldByKey(key)
  return field?.isCore ?? false
}

export function getCoreFieldOrder(key: string): number | undefined {
  const coreIndex = CORE_QUICK_START_FIELDS.findIndex((f) => f.key === key)
  return coreIndex >= 0 ? coreIndex + 1 : undefined
}

// Get next incomplete field from either core or all fields
export function getNextIncompleteField(completedFieldKeys: string[], coreOnly = false): QuickStartField | undefined {
  const fields = coreOnly ? CORE_QUICK_START_FIELDS : QUICK_START_FIELDS
  return fields.find((f) => !completedFieldKeys.includes(f.key))
}

export function getNextIncompleteFieldOrder(completedFieldKeys: string[], coreOnly = false): number {
  const fields = coreOnly ? CORE_QUICK_START_FIELDS : QUICK_START_FIELDS
  for (let i = 0; i < fields.length; i++) {
    if (!completedFieldKeys.includes(fields[i].key)) {
      return i
    }
  }
  return fields.length
}

// Generate conversational prompt with business name interpolation
export function getConversationalPrompt(field: QuickStartField, businessName?: string): string {
  if (!businessName) {
    return field.conversationalPrompt.replace("{business_name}", "your business")
  }
  return field.conversationalPrompt.replace("{business_name}", businessName)
}

// Field key mapping for backward compatibility
export const FIELD_KEY_ALIASES: Record<string, string> = {
  industry: "what_you_do",
  products_services: "what_you_do",
  target_market: "who_you_serve",
  goals: "primary_goal",
  current_goals: "primary_goal",
}

export function normalizeFieldKey(key: string): string {
  return FIELD_KEY_ALIASES[key] ?? key
}
